/**
 * Artemis Math Mission — round data.
 *
 * Round 0 is a fixed guided tutorial. The 9 scored rounds are generated
 * procedurally: each is a valid simplest-ratio problem — three fuel percentages
 * that sum to 100 and reduce (by GCD) to a single-digit whole-number ratio, so
 * one keypad tap per pump answers it.
 */

export const PUMP_ORDER = ['orange', 'blue', 'purple'];

// Pump body colours — exact Figma / Cuemath token values (Orange 4 / Blue 4 / Purple 4).
export const PUMP_COLOR = { orange: 0xff884c, blue: 0x33ccff, purple: 0xff80f4 };
export const PUMP_COLOR_CSS = { orange: '#ff884c', blue: '#33ccff', purple: '#ff80f4' };

export const LIVES_PER_ROUND = 3;
export const SCORED_ROUNDS = 9; // rounds after the tutorial
export const WIN_DOTS = 6; // green dots needed to win

export function gcd(a, b) {
  return b === 0 ? a : gcd(b, a % b);
}

// Fixed tutorial round — matches the Figma tutorial (25/25/50 -> 1:1:2).
export const TUTORIAL_ROUND = { id: 'tutorial', pct: [25, 25, 50], answers: [1, 1, 2] };

// Three positive parts summing to S, each in [1, maxPart]; null if impossible.
function partition3(S, maxPart) {
  const a = 1 + Math.floor(Math.random() * Math.min(maxPart, S - 2));
  const bMin = Math.max(1, S - a - maxPart);
  const bMax = Math.min(maxPart, S - a - 1);
  if (bMax < bMin) return null;
  const b = bMin + Math.floor(Math.random() * (bMax - bMin + 1));
  const c = S - a - b;
  if (c < 1 || c > maxPart) return null;
  return [a, b, c];
}

// One valid round. Percentages are multiples of a unit U = 100/S, so they sum
// to 100; the reduced ratio is `parts` (GCD forced to 1 => single-digit answers).
function makeRound() {
  const sums = [4, 5, 10, 20]; // => U = 25, 20, 10, 5
  for (let t = 0; t < 300; t++) {
    const S = sums[Math.floor(Math.random() * sums.length)];
    const U = 100 / S;
    const parts = partition3(S, 8);
    if (!parts) continue;
    if (gcd(gcd(parts[0], parts[1]), parts[2]) !== 1) continue; // already simplest
    const pct = parts.map(p => p * U);
    if (pct.some(p => p < 10 || p > 80)) continue; // keep readable
    return { pct, answers: parts, diff: Math.max(...parts) + S };
  }
  return { pct: [20, 30, 50], answers: [2, 3, 5], diff: 10 };
}

/** Generate `n` distinct scored rounds, ordered easy -> hard. */
export function generateRounds(n = SCORED_ROUNDS) {
  const seen = new Set();
  const out = [];
  let guard = 0;
  while (out.length < n && guard++ < 3000) {
    const r = makeRound();
    const key = r.pct.join(',');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  out.sort((a, b) => a.diff - b.diff);
  return out.map((r, i) => ({ id: `r${i + 1}`, pct: r.pct, answers: r.answers }));
}
